import { DeleteOutline, Visibility } from '@mui/icons-material'
import { Box, Chip, styled, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { Context, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { MainComponentStatus, IMainComponentType as Type } from 'src/enums'
import { AuthorizationType } from 'src/enums/auth'
import { permissionValue } from 'src/enums/permissionValue'
import { IEmptyStatusProps } from 'src/generated-types/empty-status'
import { IMainComponentType } from 'src/generated-types/main-component-type'
import { getFormattedDate, setToMidnight } from 'src/utils'
import {
  IGetCertificatesParams,
  invalidateCertificateCache,
  useDeleteCertificate,
  useGetCertificates,
} from '../api/Certificates'
import { invalidateMainComponentCache } from '../api/main-component'
import NoCertificates from '../assets/svg/no-certificate.svg'
import { ReactComponent as PlusTimerOutlined } from '../assets/svg/PlusTimerOutlined.svg'
import { useDialogCtx } from '../hooks/context-hooks'
import { ScreenTitleBar } from '../screens/localities/components/ScreenTitleBar'
import { AuthorizationProvider } from './common/Authorization'
import CertificateExtensionViewer from './common/CertificateExtensionViewer'
import { GridActionButton } from './common/GridActionButton'
import { CreateCertificateExtensionDialog } from './main-component/CreateCertificateExtensionDialog'
import { EmptyStatusCard } from './mooring/EmptyStatusCard'
import GenericTable from './table/GenericTable'

export interface ICertificatesTableProps extends IEmptyStatusProps {
  mainComponentId: EntityId
  mainComponentType?: IMainComponentType
  customerId: EntityId
  positionId: EntityId
  showCertificatesHeader?: boolean
  height?: number
  certificateParams: IGetCertificatesParams
  context?: Context<any> | null
  setDisableAddCertificate?: (disable: boolean) => void
  mainComponentStatus: number
}
export const CertificatesTableGeneric: React.FC<ICertificatesTableProps> = ({
  mainComponentId,
  mainComponentType,
  customerId,
  positionId,
  showCertificatesHeader: showCertificatesHeader,
  height,
  certificateParams,
  context,
  setDisableAddCertificate,
  mainComponentStatus,
}) => {
  const { t } = useTranslation(['document', 'common', 'certificate'])
  const { mutateAsync, isLoading: isDeleting, error } = useDeleteCertificate(mainComponentId)
  const { showDialog, showConfirmDialog, openFilePreviewDialog } = useDialogCtx()
  const theme = useTheme()
  const { data: certificates } = useGetCertificates(mainComponentId, certificateParams)

  const sortCertificates = (items: any[] = []) => {
    const validNonExpired: any[] = []
    const validExpired: any[] = []
    const invalid: { cert: any; originalIndex: number }[] = []
    const now = setToMidnight(new Date()).getTime()

    items.forEach((cert, idx) => {
      if (cert.isValid === true) {
        const validUntil = cert?.validUntil?.value ?? cert?.validUntil
        const expiryDate = validUntil ? new Date(validUntil).getTime() : Number.MAX_SAFE_INTEGER

        if (expiryDate >= now) {
          validNonExpired.push(cert)
        } else {
          validExpired.push(cert)
        }
      } else {
        invalid.push({ cert, originalIndex: idx })
      }
    })

    validNonExpired.sort((a, b) => {
      const aRaw = a?.validUntil
      const bRaw = b?.validUntil
      const aDateVal = aRaw?.value ?? aRaw ? new Date(aRaw?.value ?? aRaw).getTime() : Number.MAX_SAFE_INTEGER
      const bDateVal = bRaw?.value ?? bRaw ? new Date(bRaw?.value ?? bRaw).getTime() : Number.MAX_SAFE_INTEGER
      return aDateVal - bDateVal
    })

    validExpired.sort((a, b) => {
      const aRaw = a?.validUntil
      const bRaw = b?.validUntil
      const aDateVal = aRaw?.value ?? aRaw ? new Date(aRaw?.value ?? aRaw).getTime() : Number.MAX_SAFE_INTEGER
      const bDateVal = bRaw?.value ?? bRaw ? new Date(bRaw?.value ?? bRaw).getTime() : Number.MAX_SAFE_INTEGER
      return aDateVal - bDateVal
    })

    const invalidCerts = invalid.sort((a, b) => a.originalIndex - b.originalIndex).map(e => e.cert)

    return [...validNonExpired, ...validExpired, ...invalidCerts]
  }

  const sortedCertificates = useMemo(() => {
    const sorted = sortCertificates(certificates?.items || [])

    return sorted.map((cert, index) => ({
      ...cert,
      isCurrentlyActive:
        index === 0 &&
        cert.isValid === true &&
        (() => {
          const validUntil = cert?.validUntil?.value ?? cert?.validUntil
          const expiryDate = validUntil ? new Date(validUntil).getTime() : Number.MAX_SAFE_INTEGER
          const now = setToMidnight(new Date()).getTime()
          return expiryDate >= now
        })(),
    }))
  }, [certificates?.items])

  useEffect(() => {
    if (error && error['data'] == 'CanNotDeleteCertificateOfaComponentNotInService') {
      toast(t('CanNotDeleteCertificateOfaComponentNotInService', { ns: 'certificate' }), { type: 'error' })
    }
  }, [error, t])

  useEffect(() => {
    return (
      setDisableAddCertificate &&
      setDisableAddCertificate(([...(certificates?.items || [])].filter(c => c.isValid == true)?.length ?? 0) > 0)
    )
  }, [certificates, setDisableAddCertificate])

  const onDeleteCertificate = (id: EntityId) => {
    showConfirmDialog(
      t('deleteCertificate', { ns: 'certificate' }),
      t('areYouSureDeleteCertificate', { ns: 'certificate' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await mutateAsync(id, {
          onSuccess: () => {
            toast.success(t('CertificateDeletedSuccussfully', { ns: 'certificate' }))
            invalidateCertificateCache.getCertificates(mainComponentId)
            invalidateMainComponentCache.getMainComponentById(customerId, mainComponentId)
            invalidationActivityLogCache.getCageActivityLogs()

            pop()
          },
          onError: () => {
            toast.success(t('ErrorDeletingCertificate', { ns: 'certificate' }))
          },
        })
      },
    )
  }

  const isDisable = (expiresIn: number, numberOfExtensions: number) => {
    return expiresIn > 92 || numberOfExtensions >= 2
  }

  const onAddExtension = async (id: EntityId) => {
    const added = await showDialog(CreateCertificateExtensionDialog, {
      title: t('addExtension', { ns: 'certificateExtension' }),
      componentProps: {
        certificateId: id as EntityId,
        mainComponentId: mainComponentId as EntityId,
        customerId: customerId as EntityId,
        positionId: positionId as EntityId,
      },
      opts: {
        maxWidth: 'md',
        fullWidth: true,
      },
    })
    if (!added) return
  }

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'certificateNumber',
      headerName: t('cert.NR  ', { ns: 'certificate' }).toUpperCase(),
      flex: 1,
      cellClassName: 'font-weight-semi-bold',
    },
    { field: 'supplierName', headerName: t('certifiedBy', { ns: 'certificate' }), flex: 1 },
    {
      field: 'validUntil',
      headerName: t('validUntil', { ns: 'certificate' }),
      flex: 1,
      valueGetter: value => (value?.value ? getFormattedDate(value.value) : ' - '),
    },
    {
      field: 'extensions',
      headerName: t('extensions', { ns: 'certificate' }),
      flex: 1,
      hide: mainComponentType?.componentType != Type.Net,
      renderCell: params => {
        if (!Array.isArray(params.row.extensions)) return `#${params.row.extensions}`

        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            <CertificateExtensionViewer extensions={params.row.extensions} />
          </Box>
        )
      },
    },
    {
      field: 'activeStatus',
      headerName: '',
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: params =>
        params.row.isCurrentlyActive ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Chip
              label={t('active', { ns: 'common' })}
              size="small"
              color="primary"
              sx={{ height: '20px', fontWeight: 500 }}
            />
          </Box>
        ) : null,
    },
  ]

  const columns: GridColumns = useMemo(
    () => [
      ...STATIC_COLUMNS,
      {
        field: 'action',
        headerName: '',
        width: 150,
        renderCell: ({ id, row }) => (
          <Box
            justifyContent={'center'}
            gap={1}
            flexDirection={'row'}
            display={'flex'}
            width={'100%'}
            marginBottom={'2px'}
          >
            {mainComponentType?.componentType == Type.Net && (
              <GridActionButton
                variant="outlined"
                buttonIcon={<PlusTimerOutlined />}
                disabled={isDisable(row.expiresIn, row.numberOfExtensions) || !row.isCurrentlyActive}
                onClick={() => !isDisable(row.expiresIn, row.numberOfExtensions) && onAddExtension(id)}
                height="36px"
                width="36px"
                shadowWeight={0}
              />
            )}
            <GridActionButton
              buttonIcon={<Visibility />}
              variant="outlined"
              height="36px"
              width="36px"
              shadowWeight={0}
              onClick={() => openFilePreviewDialog(row?.fileUrl)}
            />
            {mainComponentStatus == MainComponentStatus.InService && (
              <AuthorizationProvider
                Context={context}
                permissions={[permissionValue.Locality.Mooring.Write, permissionValue.ServiceStation.Update]}
                type={AuthorizationType.Disabled}
              >
                <GridActionButton
                  variant="outlined"
                  buttonIcon={<DeleteOutline />}
                  disabled={!row.isDeletable || isDeleting}
                  onClick={() => onDeleteCertificate(id)}
                  height="36px"
                  width="36px"
                  shadowWeight={0}
                  sx={{ opacity: !row?.isValid && (!row.isDeletable || isDeleting) ? 0.5 : 1 }}
                />
              </AuthorizationProvider>
            )}
          </Box>
        ),
      },
    ],
    [isDeleting, mainComponentStatus, mainComponentType?.componentType],
  )

  return (
    <>
      {showCertificatesHeader && <ScreenTitleBar title={t('certificates', { ns: 'certificate' })} />}
      <StyledGenericTable
        data={sortedCertificates}
        columns={columns}
        defaultColumnOption={columns.map(c => ({ field: c.field, headerName: c.headerName as string }))}
        enablePagination={false}
        hideDefaultFooterPagination={true}
        getRowClassName={params =>
          !params?.row?.isValid ? 'disabled-row' : params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd'
        }
        getCellClassName={params => (!params?.row?.isValid && params.field != 'action' ? 'cell-disabled' : '')}
        autoHeight={false}
        id={'certificates table'}
        height={height}
        noDataComponent={() => (
          <Box height="100%">
            <EmptyStatusCard
              imageWidth="96px"
              image={NoCertificates}
              header={t('certificate-empty-status-title', { ns: 'certificate' })}
              content={t('certificate-empty-status-content', { ns: 'certificate' })}
              headerFontSize="18px"
              contentFontSize="14px"
              contentClasses="mt-2"
              contentTopMargin="10px"
              imageContainerClasses="pb-4 pt-4"
              height="100%"
            />
          </Box>
        )}
        isRowSelectable={({ row }) => row.isValid}
      />
    </>
  )
}

const StyledGenericTable = styled(GenericTable)(() => ({
  '& .disabled-row': {
    backgroundColor: '#f0f0f0',
    color: '#b0b0b0',
  },
  '& .cell-disabled': {
    opacity: 0.5,
  },
}))
